<script lang="ts" setup>
import { defineProps, defineEmits, computed } from "vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { PropType } from "@vue/runtime-core";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    required: false,
  },
  mainBtnTitle: {
    type: String,
    required: true,
  },
  mainBtnLoading: {
    type: Boolean,
    default: false,
  },
  secondaryBtnTitle: {
    type: String,
    required: false,
  },
  secondaryBtnLoading: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String as PropType<"success" | "default" | "error">,
    default: "default",
  },
});

const emit = defineEmits(["action", "secondary-action", "close"]);

const isResultStatus = computed(() =>
  ["error", "success"].includes(props.status)
);

const emitAction = () => {
  emit("action");
};

const emitSecondaryAction = () => {
  emit("secondary-action");
};

const emitClose = () => {
  emit("close");
};
</script>

<template>
  <!--
    appear анимирует появление в момент монтирования, поэтому плавное открытие
    получают сразу все места вызова BaseModal (их 37) без правок в каждом.
    Закрытие остаётся мгновенным: родитель снимает модалку через v-if, и
    <Transition> изнутри компонента до фазы leave уже не доживает.
  -->
  <Transition appear name="modal">
    <div class="base-modal">
      <div class="base-modal--window" :class="status">
        <div class="base-modal--close">
          <BaseFigureForIcon
            icon="close"
            color="white"
            size="medium"
            is-button
            @click="emitClose"
          />
        </div>
        <div class="base-modal--title">
          <p
            class="h1"
            :class="{ 'color-white': isResultStatus }"
            v-html="title"
          ></p>
        </div>
        <div v-if="text" class="base-modal--subtitle">
          <p class="caption-medium color-grey-light">
            {{ text }}
          </p>
        </div>
        <div class="base-modal--slot">
          <slot />
        </div>
        <div class="base-modal--actions">
          <div class="base-modal--main-btn">
            <BaseButton
              :title="mainBtnTitle"
              @click="emitAction"
              :loading="mainBtnLoading"
              :color="isResultStatus ? 'white' : 'primary'"
            />
          </div>
          <div class="base-modal--color-secondary-btn">
            <BaseButton
              v-if="secondaryBtnTitle"
              :title="secondaryBtnTitle"
              :color="'grey'"
              @click="emitSecondaryAction"
              :loading="secondaryBtnLoading"
            />
          </div>
        </div>
        <div v-if="isResultStatus" class="base-modal--icon color-white">
          <BaseIcon
            v-if="status === 'success'"
            name="check-success"
            folder="display"
            class="icon-check"
          />
          <BaseIcon
            v-else-if="status === 'error'"
            name="close"
            folder="display"
            class="icon-check"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.base-modal {
  z-index: 100;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    align-items: flex-end;
  }

  &--window {
    background: var(--color-bg-primary);
    padding: 32px 32px 24px;
    border-radius: 32px;
    max-width: 480px;
    width: 100%;
    min-height: 480px;
    display: flex;
    flex-flow: column;
    position: relative;
    justify-content: space-between;

    @media (max-width: 768px) {
      max-width: unset;
      border-radius: 32px 32px 0 0;
    }

    &.success {
      background: url("../../assets/img/icons/success-back.png") no-repeat;
      background-size: cover;
    }
    &.error {
      background: var(--color-count);
      background-size: cover;
    }
  }

  &--close {
    position: absolute;
    top: 0;
    right: -54px;

    @media (max-width: 768px) {
      position: fixed;
      top: 67px;
      right: 30px;
    }
  }

  &--icon {
    position: absolute;
    bottom: 133px;
    right: 23px;
    z-index: 0;
    .icon-check {
      height: 120px;
      width: 156px;
    }
  }

  &--subtitle {
    margin-top: 12px;
    max-width: 361px;
    margin-bottom: auto;
  }

  &--slot {
    margin-bottom: 20px;
    margin-top: auto;
  }

  &--actions {
    display: flex;
    flex-flow: column;
    gap: 12px;
  }
}
</style>
